import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import { useUnsavedChanges } from "../../../components/layout/UnsavedChanges";
import {
  Button,
  EmptyState,
  buttonClasses,
  failedEmptyState,
  notFoundEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { ROUTES, enquiryDetailPath } from "../../../routes/paths";
import { decodeRouteId } from "../../../routes/routeIds";
import AddEnquiryForm from "../components/AddEnquiryForm";
import EnquiryDetailSkeleton from "../components/EnquiryDetailSkeleton";
import { fullNameOf } from "../enquiryMapping";
import { toCreateEnquiryPayload, toEnquiryForm } from "../enquiryPayload";
import { useEnquiry } from "../hooks/useEnquiry";
import { useEnquiryLookups } from "../hooks/useEnquiryLookups";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { updateEnquiry } from "../services/enquiriesService";
import { NewEnquiry } from "../types/enquiries.types";
import { validateNewEnquiry } from "../validation";

/**
 * The Add enquiry form, opened on an enquiry that already exists.
 *
 * The same form component, the same validation and the same payload builder —
 * only the answers it starts with and where it goes afterwards differ. Two
 * screens sharing one form is what keeps a field added to the add screen from
 * quietly going missing from the edit one.
 *
 * The record is fetched here rather than handed over by the detail screen: an
 * edit reached by its own URL — a reload, a bookmark, a link — has to fill the
 * form just the same.
 */
const EditEnquiryPage: React.FC = () => {
  // Enciphered in the path — see `routeIds`. A token that does not decode is
  // "", which finds no enquiry and lands on the not-found screen below.
  const { enquiryId: token = "" } = useParams();
  const enquiryId = decodeRouteId(token);

  const navigate = useNavigate();
  const toast = useToast();
  const getToken = useTokenGetter();

  const { enquiry: record, status: loadStatus, reload } = useEnquiry(enquiryId);
  const lookups = useEnquiryLookups();

  // Read before the returns below, since a hook cannot sit behind one. A
  // record that arrives quickly opens straight into the form, with no
  // screenful of blocks in between.
  const phase = useLoadPhase(loadStatus === "loading");

  // `undefined` until the record lands, which is what the effect below fills.
  // Held here rather than derived from the record on every render: from the
  // first keystroke this is what the form holds, and the record is only where
  // it started.
  const [enquiry, setEnquiry] = useState<NewEnquiry | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) setEnquiry(toEnquiryForm(record));
  }, [record]);

  // What the form opened with, which is what an edit is measured against. The
  // record itself rather than a second copy of the form state: it is the same
  // mapping the effect above seeds from, so the two cannot drift.
  const opened = useMemo(
    () => (record ? toEnquiryForm(record) : null),
    [record]
  );

  // Worked out up here rather than beside the form below, since the prompt is
  // registered above the returns and has to know whether this form can be sent
  // as it stands. Nothing to check while the record is still on its way.
  const errors = enquiry ? validateNewEnquiry(enquiry) : {};
  const valid = Object.keys(errors).length === 0;

  // The form's own way of marking up what is still missing, so the rail's
  // prompt can offer to save a form that cannot be saved yet and point at why.
  const problems = useRef<(() => void) | null>(null);

  // Above the returns below, since a hook cannot sit behind one — and true only
  // once there is a record to have edited, so the wait and the not-found screen
  // hold nothing.
  useUnsavedChanges(
    !!enquiry && !!opened && JSON.stringify(enquiry) !== JSON.stringify(opened),
    {
      // The add screen's wording, put in the tense this screen is in: nothing
      // is being entered here, an existing record is being changed — and
      // "Save & add" would name something this form does not do.
      title: "Unsaved enquiry details",
      body: "You’ve changed this enquiry’s details but haven’t saved them yet. Save your changes, or they’ll be discarded.",
      confirmLabel: "Save changes",
    },
    // The Save row's own answer, handed to the prompt so its button does what
    // it says. `commit` is called through an arrow rather than passed: it is
    // declared below, and the literal is built as this renders.
    {
      canSave: valid && !saving,
      save: () => commit(),
      report: () => problems.current?.(),
    }
  );

  const handleChange = (patch: Partial<NewEnquiry>) =>
    setEnquiry((current) => (current ? { ...current, ...patch } : current));

  /**
   * Sends the edit and says whether it landed, leaving where to go next to
   * whoever asked for it: the Save row goes back to the record, the rail's
   * prompt carries on to wherever it was pointing. It must not navigate itself,
   * or the prompt's answer would take somebody somewhere they did not ask for.
   */
  const commit = async (): Promise<boolean> => {
    if (saving || !enquiry || !record) return false;

    // The dropdowns hold names; the endpoint takes the ids behind them. A name
    // with no id left to match means a catalog list changed while this form
    // sat open — worth naming the field rather than sending a broken body.
    const result = toCreateEnquiryPayload(enquiry, lookups);
    if (!result.ok) {
      toast.error(
        "Some options are out of date",
        `Pick ${result.unresolved.join(" and ")} again.`
      );
      return false;
    }

    setSaving(true);

    try {
      await updateEnquiry(getToken, record.id, result.payload);
    } catch (error) {
      toast.error(
        "Could not save these changes",
        error instanceof ApiError
          ? error.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("updating an enquiry failed:", error);
      // Left on the form with every edit still in it, which is the only place
      // they can be corrected and sent again.
      setSaving(false);
      return false;
    }

    const name = `${enquiry.firstName} ${enquiry.lastName}`.trim();
    toast.success("Enquiry updated", `${name}'s details have been saved.`);

    return true;
  };

  const handleSave = async () => {
    if (!(await commit()) || !record) return;

    // Back to the record itself, which refetches as it mounts — so what is
    // read there is what the backend stored rather than what was typed here.
    navigate(enquiryDetailPath(record.id));
  };

  // The record has to be in before the form can open on it, so the wait is the
  // detail screen's own — this is the same enquiry arriving, for a page that
  // happens to make it editable. `!enquiry` covers the single render between
  // the record landing and the effect above filling the form from it.
  if (loadStatus === "loading" || (loadStatus === "ready" && !enquiry)) {
    return <EnquiryDetailSkeleton phase={phase} />;
  }

  if (loadStatus === "failed") {
    return (
      <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
        <PageHeader
          title="Edit enquiry"
          breadcrumbs={[
            { label: "Enquiries", to: ROUTES.enquiries },
            { label: "Edit enquiry" },
          ]}
        />
        <div className="mt-5 flex-1 min-h-0 flex items-center justify-center rounded-md border border-line bg-white">
          <EmptyState
            {...failedEmptyState(
              "We could not load this enquiry",
              <Button variant="outline" size="sm" onClick={reload}>
                Try again
              </Button>
            )}
          />
        </div>
      </div>
    );
  }

  if (!record || !enquiry) {
    return (
      <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
        <PageHeader
          title="Enquiry not found"
          breadcrumbs={[
            { label: "Enquiries", to: ROUTES.enquiries },
            { label: "Enquiry not found" },
          ]}
        />
        <div className="mt-5 flex-1 min-h-0 flex items-center justify-center rounded-md border border-line bg-white">
          <EmptyState
            {...notFoundEmptyState(
              "That enquiry no longer exists",
              "There is nothing here to edit. It may have been deleted since this link was opened.",
              // A link and not a button — this goes somewhere. See the note
              // on `buttonClasses`.
              <Link
                to={ROUTES.enquiries}
                className={buttonClasses({ variant: "secondary", size: "sm" })}
              >
                Back to enquiries
              </Link>
            )}
          />
        </div>
      </div>
    );
  }

  const name = fullNameOf(record);

  return (
    // No bottom padding on purpose: the action bar sits flush on the foot of
    // the shell, and the form supplies the space under its own buttons.
    <div className="min-h-full flex flex-col px-4 pt-5 sm:px-6 lg:px-8 3xl:px-12 3xl:pt-8">
      <PageHeader
        title="Edit enquiry"
        breadcrumbs={[
          { label: "Enquiries", to: ROUTES.enquiries },
          // Back to the record rather than to the list: it is where this was
          // opened from, and where cancelling and saving both land.
          { label: name, to: enquiryDetailPath(record.id) },
          { label: "Edit" },
        ]}
      />

      <AddEnquiryForm
        enquiry={enquiry}
        onChange={handleChange}
        errors={errors}
        lookups={lookups}
        saving={saving}
        saveLabel="Save changes"
        onSave={handleSave}
        onCancel={() => navigate(enquiryDetailPath(record.id))}
        problems={problems}
      />
    </div>
  );
};

export default EditEnquiryPage;
